import React, { useRef, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useOrganization } from 'contexts/organization'
import { compact, pruneBy } from 'utilities/array'
import { size } from 'utilities/object'
import Modal from 'modals/dialog'
import Form from 'components/form/controller'
import OneOfField from 'components/form/field/one-of'
import EntitiesField from 'components/form/field/entities'
import PeopleField from 'components/form/field/people'
import SelectField from 'components/form/field/select'
import Message from 'components/message'
import Actions from 'components/form/actions'
import { ButtonSubmit } from 'components/button'

const OneWho = ({ next, outsideFormData, formData, setFormData, salt }) => {
    const { formatMessage } = useIntl()

    const groupsRef = useRef()
    const peopleRef = useRef()

    const { organization } = useOrganization()

    const getUniqueMemberCount = groups => pruneBy(groups.flatMap(group => group?.members ?? [])).length

    const [who, setWho] = useState(formData?.who ?? 'all')
    const [groups, setGroups] = useState(formData?.groups ?? [])
    const [people, setPeople] = useState(formData?.people ?? [])
    const [accountStatuses, setAccountStatuses] = useState(formData?.accountStatuses ?? 'active')
    const [userCount, setUserCount] = useState(
        (who === 'all') ? (organization?.userCount ?? 0) :
        (who === 'groups') ? getUniqueMemberCount(groups) :
        (who === 'people') ? people.length :
        0
    )

    const calculateAllUserCount = accountStatuses => {
        const userCount = accountStatuses === 'all' ?
            (organization?.totalUserCount ?? 1)
            : accountStatuses === 'inactive' ?
                (organization?.totalUserCount - (organization?.userCount ?? 1))
                : (organization?.userCount ?? 1)

        setUserCount(userCount)
    }

    const update = () => {
        setFormData(formData => {
            if(who === 'people' && people.length === 1 && people?.[0]?.status?.active === false) {
                formData.accountStatuses = 'inactive'
            }

            return ({
                ...formData,
                who,
                groups,
                people
            })
        })
        next()
    }

    return (
        <Form
            layout="vertical"
            onSubmit={update}>
            {({ errors, trigger }) => (
                <>
                    <Modal.Body>
                        {(outsideFormData?.who !== 'people') && (
                            <>
                                <OneOfField
                                    name="who"
                                    label={formatMessage({
                                        id: 'label_include_who',
                                        defaultMessage: 'Who should be included?'
                                    })}
                                    field={{
                                        value: who,
                                        required: true,
                                        include: 'always',
                                        options: compact([
                                            {
                                                value: 'all',
                                                label: formatMessage({
                                                    id: 'organization_everyone_in',
                                                    defaultMessage: 'Everyone in {name}'
                                                }, organization),
                                                ...((!!outsideFormData?.who && outsideFormData.who !== 'all') ? { disabled: true } : null)
                                            },
                                            {
                                                value: 'groups',
                                                label: formatMessage({
                                                    id: 'groups_members_of_generic',
                                                    defaultMessage: 'Members of groups'
                                                }),
                                                ...((!!outsideFormData?.who && outsideFormData.who !== 'groups') ? { disabled: true } : null),
                                                controlRef: groupsRef,
                                                content: (
                                                    <EntitiesField
                                                        name="groups"
                                                        label={false}
                                                        field={{ value: groups }}
                                                        picker={{
                                                            heading: formatMessage({
                                                                id: 'groups_action_pick',
                                                                defaultMessage: 'Pick groups'
                                                            }),
                                                            query: {
                                                                key: ['groups'],
                                                                path: '/groups',
                                                                filterOptions: {
                                                                    overrides: {
                                                                        search: {
                                                                            controlProps: {
                                                                                placeholder: formatMessage({
                                                                                    id: 'groups_action_find',
                                                                                    defaultMessage: 'Find groups'
                                                                                })
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                        onChange={({ groups }) => {
                                                            setGroups(groups)

                                                            if(who === 'groups') {
                                                                setUserCount(getUniqueMemberCount(groups))
                                                            }
                                                        }}
                                                        onUnset={() => {
                                                            setGroups([])
                                                            setUserCount(0)
                                                        }}
                                                        {...((outsideFormData?.who === 'groups') ? { enabled: false } : null)}
                                                        ref={groupsRef} />
                                                )
                                            },
                                            {
                                                value: 'people',
                                                label: formatMessage({
                                                    id: 'people_individuals',
                                                    defaultMessage: 'Individual people'
                                                }),
                                                ...((!!outsideFormData?.who && outsideFormData.who !== 'people') ? { disabled: true } : null),
                                                content: (
                                                    <PeopleField
                                                        name="people"
                                                        label={false}
                                                        field={{ value: people }}
                                                        onChange={({ people }) => {
                                                            setPeople(people)

                                                            if(who === 'people') {
                                                                setUserCount(people.length)
                                                            }
                                                        }}
                                                        onUnset={() => {
                                                            setPeople([])
                                                            setUserCount(0)
                                                        }}
                                                        ref={peopleRef} />
                                                ),
                                                controlRef: peopleRef
                                            }
                                        ])
                                    }}
                                    onChange={({ who }) => {
                                        setWho(who)

                                        if(who === 'all') {
                                            calculateAllUserCount(accountStatuses)
                                        }

                                        if(who === 'groups') {
                                            setUserCount(getUniqueMemberCount(groups))
                                        }

                                        if(who === 'people') {
                                            setUserCount(people.length)
                                        }
                                    }}
                                    salt={salt} />
                                <SelectField
                                    name="accountStatuses"
                                    label={formatMessage({
                                        id: 'noun_account_status',
                                        defaultMessage: 'Account status'
                                    })}
                                    field={{
                                        value: (who !== 'people') ? 'active' : accountStatuses,
                                        include: 'always',
                                        allowEmptyOption: false
                                    }}
                                    options={[
                                        {
                                            key: 'active',
                                            text: formatMessage({
                                                id: 'noun_account_status_activated_plural',
                                                defaultMessage: 'Active accounts'
                                            }),
                                            value: 'active'
                                        },
                                        {
                                            key: 'inactive',
                                            text: formatMessage({
                                                id: 'noun_account_status_deactivated_plural',
                                                defaultMessage: 'Deactivated accounts'
                                            }),
                                            value: 'inactive'
                                        },
                                        {
                                            key: 'all',
                                            text: formatMessage({
                                                id: 'all_plural',
                                                defaultMessage: 'All'
                                            }),
                                            value: 'all'
                                        }
                                    ]}
                                    enabled={(who !== 'people')}
                                    onChange={({ accountStatuses }) => {
                                        setAccountStatuses(accountStatuses)

                                        setFormData(formData => ({
                                            ...formData,
                                            accountStatuses
                                        }))

                                        if(who === 'all') {
                                            calculateAllUserCount(accountStatuses)
                                        }
                                    }}
                                    whistle={!who ? who !== 'people' : 'undecided'}
                                    salt={salt} />
                            </>
                        )}
                        {(!!userCount && !(who === 'groups' && accountStatuses !== 'active')) && (
                            <Message
                                type="info"
                                message={formatMessage({
                                    id: 'people_export_summary',
                                    defaultMessage: '{count, plural, =0 {} =1 {Export your own user and profile data.} other {Export {count} users and their profile data.}}'
                                }, { count: userCount })} />
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Actions>
                            <ButtonSubmit
                                className="constructive"
                                disabled={[
                                    (who === 'groups' && !groups.length),
                                    (who === 'people' && !people.length),
                                    !!size(errors)
                                ].some(Boolean)}
                                ref={trigger}>
                                <FormattedMessage
                                    id="action_continue"
                                    defaultMessage="Continue" />
                            </ButtonSubmit>
                        </Actions>
                    </Modal.Footer>
                </>
            )}
        </Form>
    )
}

export default OneWho